<template>
  <agenda-card
    class="nested-agenda-session is-relative"
    @open="onCardOpen"
    :startTime="startTime"
    :endTime="endTime"
    :color="agendaPathColor"
    :hasCollapse="hasCollapse"
    :hide-session-time="hideSessionTime"
    :show-live-indicator="showLiveIndicator"
    :is-highlighted="isHighlighted"
    :session-time-with-date="sessionTimeWithDate"
  >
    <template>
      <b-loading :active="isRequesting" :can-cancel="false" :is-full-page="false"></b-loading>
      <h2 class="agenda-session-title" :class="{ 'is-scientific': isScientificTheme, 'is-highlighted': isHighlighted }">{{ title }}</h2>
      <p class="agenda-session-subtitle" v-if="isDefaultTheme">{{ subtitle }}</p>
      <div class="agenda-session-path-tags-container" v-if="isScientificTheme">
        <agenda-session-path-tag :path-tag="agendaPath"></agenda-session-path-tag>
      </div>
      <template v-if="isDefaultTheme">
        <template v-if="isSearchResult && searchResultSpeakers && searchResultSpeakers.length">
          <div class="agenda-speakers-list-container">
            <div v-for="speaker in searchResultSpeakers" :key="speaker.id">
              <agenda-session-speaker-list-item :speaker="speaker" :is-search-result="true"></agenda-session-speaker-list-item>
            </div>
          </div>
        </template>
        <template v-else-if="shortSpeakersList && shortSpeakersList.length">
          <div class="agenda-speakers-list-container">
            <agenda-session-speaker-list-item
              v-for="speaker in shortSpeakersList"
              :key="speaker.id"
              :speaker="speaker"
              :is-search-result="false"
            ></agenda-session-speaker-list-item>
          </div>
        </template>
      </template>
      <template v-else>
        <template v-if="isSearchResult && searchResultSpeakers && searchResultSpeakers.length">
          <div class="agenda-speakers-scientific-list-container">
            <agenda-session-speaker-scientific-list-item
              v-for="speaker in searchResultSpeakers"
              :key="speaker.id"
              :speaker="speaker"
              :is-search-result="true"
            ></agenda-session-speaker-scientific-list-item>
          </div>
        </template>
        <template v-else-if="shortSpeakersList && shortSpeakersList.length">
          <div class="agenda-speakers-scientific-list-container">
            <agenda-session-speaker-scientific-list-item
              v-for="speaker in shortSpeakersList"
              :key="speaker.id"
              :speaker="speaker"
              :is-search-result="true"
            ></agenda-session-speaker-scientific-list-item>
          </div>
        </template>
      </template>
      <template v-if="isScientificTheme">
        <div class="agenda-session-scientific-short-description" v-if="description" v-html="description"></div>
        <div class="agenda-session-scientific-room-container">
          <div class="agenda-session-scientific-room-label">{{ agendaPlace.value }}</div>
          <div class="agenda-session-scientific-room-dot">•</div>
          <a class="agenda-session-scientific-room-button" @click="openAgendaSessionDetailsPopup">{{ $t("common.read_more_text") }}</a>
        </div>
      </template>
    </template>

    <template #actions>
      <div class="columns is-gapless is-vcentered is-mobile">
        <!-- Video call link -->
        <div v-if="streamButtonVisible" class="column is-narrow">
          <b-button
            @click.stop="openLiveStreamLink"
            type="is-outlined"
            class="button agenda-session-live-button"
            :class="{ 'has-live-tag': liveIndicatorVisible }"
          >
            <div class="agenda-session-live-button-content">
              <span>
                {{ $t("common.join") }}
              </span>
              <span v-if="liveIndicatorVisible">{{ ` ${$t("common.live")}` }}</span>
              <stream-icon class="stream-icon has-text-primary"></stream-icon>
            </div>
            <div class="live-tag" v-if="liveIndicatorVisible">
              <div class="live-stream-blob"></div>
              <div class="live-text">
                {{ $t("common.live") }}
              </div>
            </div>
          </b-button>
        </div>
        <!-- MySchedule / Tickets -->
        <div class="column is-narrow ml-1" v-if="attendButtonVisible">
          <div class="icon-container" @click.stop="onAttendButtonClick">
            <tick-icon v-if="isAttending" class="svg-icon has-fill-primary pointer-hover"></tick-icon>
            <ticket-icon v-else-if="requiresTicket" class="svg-icon has-fill-primary pointer-hover"></ticket-icon>
            <add-icon v-else class="svg-icon has-fill-primary pointer-hover"></add-icon>
          </div>
        </div>
      </div>
    </template>

    <template #collapse>
      <div class="content agenda-section" v-if="ratingVisible">
        <div class="is-flex is-justify-content-flex-end">
          <div class="is-flex is-flex-direction-column is-align-items-center agenda-session-rating-button" @click="openAgendaSessionRating">
            <stars-rating :disabled="true" :value="ratingValue"></stars-rating>
            <i18n
              path="agenda.reviews_uppercase"
              tag="div"
              class="rating-text has-text-weight-semibold"
              style="margin-top: 4px; font-size: 0.7rem"
              v-if="ratingEnabled"
            >
              {{ ratingReviews }}
            </i18n>
            <i18n
              path="agenda.session_rating_available_from"
              tag="div"
              class="rating-text has-text-weight-semibold"
              style="margin-top: 4px; font-size: 0.7rem"
              v-else
            >
              {{ ratingTime }}
            </i18n>
          </div>
        </div>
      </div>

      <div class="content agenda-section">
        <div v-if="requiresTicket" class="is-flex is-justify-content-flex-end has-text-primary is-size-5">
          <i18n path="agenda.session_ticket_seats_left" tag="div" v-if="ticketsLeft > 0">
            {{ ticketsLeft }}
          </i18n>
          <p v-else-if="ticketsLeft === 0" class="is-danger">
            {{ $t("agenda.session_ticket_no_seats_left") }}
          </p>
        </div>
      </div>
      <div class="content agenda-section agenda-description">
        <p v-if="description" v-html="description"></p>
      </div>

      <div class="content" v-if="sessionDiscussionVisible">
        <div class="button is-fullwidth button-text is-primary is-outlined" @click="openAgendaSessionDiscussion">
          {{ $t("agenda.questions") }}
        </div>
      </div>

      <div class="columns">
        <div class="column" :class="speakersAttendeesColumnClass">
          <div v-if="speakers.length > 0" class="agenda-section">
            <speaker-list :session="session" :column-class="speakersColumnClass"></speaker-list>
          </div>

          <div v-if="attendeesVisible" class="agenda-section">
            <h3 class="title is-4 mb-2">{{ $t("agenda.title_attendees") }}</h3>
            <list-loading-indicator v-if="attendees.length === 0" :active="areAttendeesLoading"></list-loading-indicator>
            <attendees-list
              v-if="attendees.length > 0"
              @open-user-modal="$emit('open-user-modal', $event)"
              @open-agenda-session-attendees="
                $emit('open-agenda-session-attendees', {
                  componentId: agendaComponentId,
                  agendaSessionId: agendaSessionId,
                })
              "
              :attendees="attendees"
              :total-count="attendeesTotalCount"
            ></attendees-list>
            <div v-if="attendees.length === 0 && !areAttendeesLoading" class="subtitle has-text-grey">
              {{ $t("agenda.empty_attendees_placeholder") }}
            </div>
          </div>
        </div>
        <div class="column" :class="speakersAttendeesColumnClass" v-if="customSponsorVisible">
          <session-sponsor-support :sponsor-with-support="customSponsor"></session-sponsor-support>
        </div>
      </div>

      <div v-if="attachments.length > 0" class="agenda-section">
        <h3 class="title is-4 mb-2">{{ $t("agenda.title_attached_files") }}</h3>
        <attachment-list :files="files" :links="links" column-class="is-half"></attachment-list>
      </div>
    </template>
  </agenda-card>
</template>

<script>
import StreamIcon from "@/assets/icon_live_stream.svg";
import AddIcon from "@/assets/tickets/icon_add_circle.svg";
import TicketIcon from "@/assets/tickets/icon_ticket.svg";
import TickIcon from "@/assets/tickets/icon_tick_circle.svg";
import AgendaCard from "@/shared/components/agenda/AgendaCard";
import SpeakerList from "@/widgets/agenda/components/sessions/SpeakerList";
import AttendeesList from "@/widgets/agenda/components/sessions/AttendeesList";
import AttachmentList from "@/widgets/agenda/components/sessions/AttachmentList";
import StarsRating from "@/shared/components/common/rating/StarsRating";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import ListLoadingIndicator from "@/shared/components/common/loading/ListLoadingIndicator";
import Constants from "@/widgets/shared/constants";
import { composeAgendaSessionRatingTime } from "@/shared/utils";
import SessionSponsorSupport from "@/widgets/agenda/components/custom/SessionSponsorSupport";
import AgendaSessionSpeakerListItem from "@/widgets/agenda/components/speakers/AgendaSessionSpeakerListItem";
import AgendaSessionPathTag from "@/widgets/agenda/components/sessions/AgendaSessionPathTag";
import AgendaSessionSpeakerScientificListItem from "@/widgets/agenda/components/speakers/AgendaSessionSpeakerScientificListItem";
import { pluckError, pluckErrorCode } from "@/widgets/shared/utils";
import { isLiveWebinarUrl } from "@/shared/utils/event-utils";
import { WidgetViewTypes } from "@/widgets/shared/store/modules/widget-view";

export default {
  name: "NestedAgendaSession",

  props: ["session", "hideSessionTime", "showLiveIndicator", "hideLiveStreamButtons", "sessionRatings", "customSponsor", "searchQuery"],

  data() {
    return {
      isRequesting: false,
    };
  },

  components: {
    AgendaSessionSpeakerScientificListItem,
    AgendaSessionPathTag,
    AgendaSessionSpeakerListItem,
    StreamIcon,
    TicketIcon,
    AddIcon,
    TickIcon,
    AgendaCard,
    SpeakerList,
    AttachmentList,
    AttendeesList,
    ListLoadingIndicator,
    StarsRating,
    SessionSponsorSupport,
  },

  methods: {
    ...mapActions("agendaSessionTickets", ["buyTicket", "returnTicket"]),
    ...mapActions("schedule", ["removeSessionFromSchedule", "addSessionToSchedule"]),
    ...mapMutations("sessions", ["setFreePlacesForSession"]),
    ...mapActions("agendaSessionAttendees", ["loadAttendeesFromSession"]),
    ...mapActions("agendaSessionRatings", ["fetchRating", "fetchMyRating"]),
    ...mapMutations("agendaSessionRatings", ["clearRatingError"]),
    ...mapMutations("agendaSessionAttendees", ["clearAttendeesError"]),
    ...mapMutations(["setRequestRefreshToken"]),
    ...mapMutations("errorModal", ["openErrorModal"]),
    ...mapMutations("modals", ["openSessionRatingModal", "openSessionDetailsModal"]),
    ...mapMutations("widgetView", ["changeActiveView"]),

    onAttendButtonClick() {
      if (!this.token) {
        this.emitOpenAuth();
      } else {
        if (!this.isRequesting) {
          this.isRequesting = true;
          if (this.isAttending) {
            if (this.requiresTicket) {
              this.returnTicket({
                agendaSession: this.session,
                ticketId: this.sessionTicket.id,
              })
                .catch(e => {
                  this.handleError(e);
                })
                .finally(() => (this.isRequesting = false));
            } else {
              this.removeSessionFromSchedule(this.session)
                .catch(e => {
                  this.handleError(e);
                })
                .finally(() => (this.isRequesting = false));
            }
          } else {
            if (this.requiresTicket) {
              this.buyTicket({
                agendaSession: this.session,
                ticketId: this.sessionTicket.id,
              })
                .catch(e => {
                  this.handleError(e);
                })
                .finally(() => (this.isRequesting = false));
            } else {
              this.addSessionToSchedule(this.session)
                .catch(e => {
                  this.handleError(e);
                })
                .finally(() => (this.isRequesting = false));
            }
          }
        }
      }
    },

    handleError(error) {
      const errorCode = pluckErrorCode(error);
      if (errorCode === Constants.REST_TOKEN_EXPIRED) {
        this.setRequestRefreshToken(true);
      } else {
        this.showError(error);
      }
    },

    showError(error) {
      const errorCode = pluckErrorCode(error);
      const errorMsg = pluckError(error);
      if (errorCode === Constants.AGENDA_SESSION_TICKET_LIMIT_EXCEEDED_ERROR_CODE) {
        this.setFreePlacesForSession({ sessionId: this.session.id, freePlaces: 0 });
      }
      this.openErrorModal(errorMsg);
    },

    onCardOpen() {
      if (this.theme === Constants.WIDGET_THEME_TYPES.SCIENTIFIC) {
        if (this.lpMode) {
          this.openSessionDetailsModal(this.session.id);
        } else {
          this.changeActiveView({
            type: WidgetViewTypes.SESSION_DETAILS,
            componentId: this.$root.componentId,
            data: { agendaSessionId: this.session.id }
          });
        }
      } else {
        this.loadAttendeesFromSession({ agendaSessionId: this.agendaSessionId });
        this.fetchRating({ componentId: this.agendaComponentId, agendaSessionId: this.agendaSessionId });
      }
    },

    openLiveStreamLink() {
      if (this.liveUrl) {
        if (isLiveWebinarUrl(this.liveUrl)) {
          if (this.lpMode) {
            this.emitOpenAuthorizedLiveWebinarStream(this.liveUrl);
          } else {
            window.open(this.liveUrl, "_blank");
          }
        } else {
          window.open(this.liveUrl, "_blank");
        }
      }
    },

    openAgendaSessionRating() {
      if (this.ratingEnabled) {
        if (this.token) {
          const ratings = this.sessionRatings;
          this.openSessionRatingModal(
            ratings.sort((a, b) => a.order - b.order),
            this.agendaSessionId,
            this.agendaComponentId,
          );
        } else {
          this.emitOpenAuth();
        }
      }
    },

    openAgendaSessionDiscussion() {
      if (this.token) {
        this.$emit("open-agenda-session-questions", {
          componentId: this.agendaComponentId,
          agendaSessionId: this.agendaSessionId,
        });
      } else {
        this.emitOpenAuth();
      }
    },

    openAgendaSessionDetailsPopup() {},

    emitOpenAuth() {
      this.$emit("open-auth");
    },

    emitOpenAuthorizedLiveWebinarStream(url) {
      this.$emit("open-auth-lw-stream", url);
    },
  },

  computed: {
    ...mapGetters("agendaSessionTickets", ["hasTicketForSession"]),
    ...mapGetters("schedule", ["isAttendingSession"]),
    ...mapState([
      "eventId",
      "timezone",
      "constantComponentId",
      "highlightedSessionIds",
      "componentIdsWithRatingAlwaysEnabled",
      "token",
      "lpMode",
      "questionsDisabled",
      "currentTime",
      "theme",
      "speakersCountOnPreview",
      "sessionTimeWithDate",
      "livewebinarOnlyForAuthorized",
    ]),
    ...mapGetters("agendaSessionAttendees", ["attendeesFromSession", "attendeesCountFromSession", "isLoading"]),
    ...mapState("agendaSessionAttendees", { agendaSessionAttendeesError: "error" }),
    ...mapGetters("agendaSessionRatings", ["getRating", "getRatingError"]),

    agendaSessionId() {
      return this.session.id;
    },

    agendaComponentId() {
      return this.session.event_component_id;
    },

    startTime() {
      return this.session.time_start;
    },

    endTime() {
      return this.session.time_end;
    },

    title() {
      return this.session.name;
    },

    subtitle() {
      return `${this.session.tag_agenda_place.value}, ${this.session.tag_agenda_path.value}`;
    },

    agendaPlace() {
      return this.session.tag_agenda_place;
    },

    agendaPath() {
      return this.session.tag_agenda_path;
    },

    agendaPathColor() {
      return this.agendaPath.hex;
    },

    description() {
      return this.session.description && this.$options.filters.linkify(this.session.description);
    },

    liveUrl() {
      return this.session.video_call_url;
    },

    isAttending() {
      return !!((this.requiresTicket && this.hasTicket) || (!this.requiresTicket && this.isAttendingSession(this.session)));
    },

    requiresTicket() {
      return this.session.agenda_session_tickets.length;
    },

    sessionTicket() {
      return this.requiresTicket && this.session.agenda_session_tickets[0];
    },

    ticketsLeft() {
      return this.sessionTicket && this.sessionTicket.tickets_left;
    },

    hasTicket() {
      return this.hasTicketForSession(this.session.id);
    },

    speakers() {
      return [...this.session.speakers].sort((a, b) => {
        if (a.vip !== b.vip) {
          return a.vip ? -1 : 1;
        } else {
          return a.order - b.order;
        }
      });
    },

    shortSpeakersList() {
      return this.speakers && this.speakers.slice(0, this.speakersCountOnPreview);
    },

    files() {
      return this.session.session_attachments;
    },

    links() {
      return this.session.agenda_session_external_links;
    },

    attachments() {
      return [...this.files, ...this.links];
    },

    attendees() {
      return this.attendeesFromSession(this.agendaSessionId);
    },

    attendeesTotalCount() {
      return this.attendeesCountFromSession(this.agendaSessionId);
    },

    areAttendeesLoading() {
      return this.isLoading(this.agendaSessionId);
    },

    attendButtonVisible() {
      return !this.session.attend_button_hidden && this.lpMode && !this.constantComponentId;
    },

    attendeesVisible() {
      return this.token && this.lpMode && !this.constantComponentId;
    },

    liveIndicatorVisible() {
      if (this.currentTime && this.showLiveIndicator) {
        return new Date(this.startTime) < this.currentTime && this.currentTime < new Date(this.endTime);
      } else {
        return false;
      }
    },

    streamButtonVisible() {
      return !this.hideLiveStreamButtons && this.liveUrl;
    },

    sessionDiscussionVisible() {
      return this.lpMode && this.session.agenda_discussion_on && !this.questionsDisabled;
    },

    ratingEnabled() {
      if (this.componentIdsWithRatingAlwaysEnabled) {
        if (this.componentIdsWithRatingAlwaysEnabled.some(componentId => componentId === this.agendaComponentId)) {
          return true;
        }
      }
      return new Date(this.startTime) < this.currentTime;
    },

    ratingVisible() {
      return this.lpMode && this.session.ratings_visible;
    },

    ratingObject() {
      return this.getRating(this.agendaSessionId);
    },

    ratingValue() {
      return this.ratingObject && this.ratingObject.rating;
    },

    ratingTime(date) {
      const timestamp = new Date(this.startTime).getTime();
      return composeAgendaSessionRatingTime(timestamp, this.timezone);
    },

    ratingReviews() {
      return this.ratingObject && this.ratingObject.reviews;
    },

    ratingError() {
      return this.getRatingError(this.agendaSessionId);
    },

    customSponsorVisible() {
      return !!this.customSponsor;
    },

    speakersAttendeesColumnClass() {
      if (this.customSponsorVisible) {
        return "is-half";
      } else {
        return "";
      }
    },

    speakersColumnClass() {
      if (this.customSponsorVisible) {
        return "is-full";
      } else {
        return "is-half";
      }
    },

    isSearchResult() {
      return !!this.searchQuery;
    },

    searchResultSpeakers() {
      return (
        this.isSearchResult &&
        this.speakers.filter(speaker => `${speaker.first_name} ${speaker.last_name}`.toLowerCase().includes(this.searchQuery.toLowerCase()))
      );
    },

    hasCollapse() {
      return this.theme !== Constants.WIDGET_THEME_TYPES.SCIENTIFIC;
    },

    isDefaultTheme() {
      return this.theme === Constants.WIDGET_THEME_TYPES.DEFAULT;
    },

    isScientificTheme() {
      return this.theme === Constants.WIDGET_THEME_TYPES.SCIENTIFIC;
    },

    isHighlighted() {
      return this.highlightedSessionIds.includes(this.session.id);
    },

    childrenSessionsPagerBindings() {
      return {
        sessions: this.session.agendaSessionChildren,
        // hideDays: this.hideDays,
        // hideSessionTime: this.hideSessionTime,
        // showLiveIndicator: this.showLiveIndicator,
        // pageSize: this.pageSize,
        // hideLiveStreamButtons: this.hideLiveStreamButtons,
        // sessionRatings: this.activeAgendaRatings,
        // customSponsors: this.customSponsors,
        // searchQuery: this.search,
        // parentId: this.anchorParentId,
        hideDays: false,
        hideSessionTime: false,
        showLiveIndicator: false,
        pageSize: 100000,
        hideLiveStreamButtons: false,
        sessionRatings: [],
        customSponsors: this.customSponsor,
        searchQuery: this.searchQuery,
        parentId: "lmao",
      };
    },
  },

  watch: {
    agendaSessionAttendeesError(newVal) {
      if (newVal) {
        this.handleError(newVal);
        this.clearAttendeesError();
      }
    },

    ratingError(newVal) {
      if (newVal) {
        this.handleError(newVal);
        this.clearRatingError(this.agendaSessionId);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.content {
  white-space: pre-wrap;
}

.icon-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
}

.pointer-hover:hover {
  cursor: pointer;
}

.button-text {
  font-size: 18px;
  font-weight: bolder;
}

.nested-agenda-session {
  margin-left: 48px;
}
</style>
